import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowRight, AlertTriangle, MailCheck } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const PROCESSED_KEY = "haruki_processed_magic_token";

/**
 * /auth/verify?token=...
 *
 * Two-stage magic-link confirmation page.
 *
 * 1. On mount, a READ-ONLY GET /api/auth/magic-link/status?token=... runs to
 *    classify the token (valid | consumed | expired | invalid). This call has
 *    NO side effects — URL pre-fetchers (Outlook Safe Links, Barracuda,
 *    Proofpoint, Mimecast, etc.) can hit it freely without burning the token.
 *
 * 2. If status is anything other than "valid", an error card renders immediately
 *    with a self-service "Send me a fresh link" button (uses the dead token as
 *    proof of email ownership — no re-typing required).
 *    If status is "valid", the "Confirm your sign-in" card renders and waits
 *    for the user to click "Continue to Haruki" — which then fires the POST
 *    that actually consumes the token. Scanners running headless JS won't
 *    click a button, so the token survives until the real user confirms.
 *
 * Industry-standard pattern (Slack/Notion/Linear/Vercel/GitHub).
 */
export default function MagicLinkVerify() {
  const [params] = useSearchParams();
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState("checking");   // checking | valid | consumed | expired | invalid
  const [emailMasked, setEmailMasked] = useState(null);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const token = params.get("token");

  useEffect(() => {
    if (!token) {
      window.location.replace("/");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/magic-link/status", { params: { token } });
        if (cancelled) return;
        setStatus(r.data?.status || "invalid");
        setEmailMasked(r.data?.email_masked || null);
      } catch (_) {
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleConfirm = async () => {
    if (verifying) return;
    // Defense-in-depth dedup (handles double-click on the button)
    if (sessionStorage.getItem(PROCESSED_KEY) === token) return;
    sessionStorage.setItem(PROCESSED_KEY, token);

    setVerifying(true);
    try {
      const r = await api.post("/auth/magic-link/verify", { token });
      const user = r.data.user;

      const intakeId = sessionStorage.getItem("haruki_intake_id");
      if (intakeId) {
        try {
          await api.post("/trial/link", { intake_id: intakeId });
        } catch (linkErr) {
          console.warn("Trial link failed:", linkErr?.response?.data || linkErr?.message);
        } finally {
          sessionStorage.removeItem("haruki_intake_id");
        }
      }
      sessionStorage.removeItem("haruki_signin_email_hint");

      // ALWAYS re-fetch the authenticated user via the session cookie before
      // redirecting: /auth/me is the authority (runs admin promotion + reflects
      // any trial-link role change). Verify response is only the fallback.
      let finalUser = user;
      try {
        const me = await api.get("/auth/me");
        finalUser = me.data;
      } catch (_) { /* fall back to verify response */ }
      let dest = "/role";
      if (!finalUser.role) dest = "/role";
      else if (finalUser.role === "platform_admin") dest = "/platform";
      else if (finalUser.role === "demo_admin") dest = "/admin";
      else if (finalUser.role === "agency_admin") dest = "/agency";
      else if (!finalUser.disclosure_acknowledged_at && finalUser.role === "service_member") dest = "/disclosure";
      else dest = "/app";
      window.location.replace(dest);
    } catch (e) {
      setError(e?.response?.data?.detail || "This sign-in link could not be verified.");
      setVerifying(false);
      sessionStorage.removeItem(PROCESSED_KEY);
    }
  };

  const handleResend = async () => {
    if (resendState !== "idle") return;
    setResendState("sending");
    try {
      await api.post("/auth/magic-link/resend", { token });
      setResendState("sent");
    } catch (_) {
      // Enumeration-safe backend always returns ok — surface a sent state anyway
      setResendState("sent");
    }
  };

  if (!token) return null;

  // While checking status — show a neutral spinner
  if (status === "checking") {
    return (
      <div className="dark flex min-h-screen flex-col items-center justify-center bg-background text-foreground">
        <Loader2 className="mb-3 h-5 w-5 animate-spin text-muted-foreground" />
        <div className="text-sm text-muted-foreground">Checking your sign-in link…</div>
      </div>
    );
  }

  // POST-verify-failure error (e.g. race condition where token expired between
  // page load and click)
  if (error) {
    return (
      <div className="dark flex min-h-screen flex-col items-center justify-center bg-background px-4 text-foreground">
        <Card className="haruki-fade-in w-full max-w-md">
          <CardContent className="space-y-4 p-8 text-center">
            <div className="text-sm" data-testid="magic-verify-error">{error}</div>
            <Button
              onClick={() => window.location.replace("/signin")}
              size="lg" className="w-full"
              style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              data-testid="magic-verify-error-back-button"
            >
              Return to sign-in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pre-check error states (consumed | expired | invalid) — error card renders
  // IMMEDIATELY on page load. No click required.
  if (status === "consumed" || status === "expired" || status === "invalid") {
    const msg =
      status === "consumed"  ? "This sign-in link has already been used. This often happens when your email provider’s security scanner (Microsoft Safe Links, Barracuda, Proofpoint) opens the link to check it before you do."
    : status === "expired"   ? "This sign-in link has expired."
    : /* invalid */            "This sign-in link is invalid or has been revoked.";
    // Only offer self-service resend when the backend recognised the token
    // (i.e. we have the associated email) — invalid tokens have no email.
    const canResend = status !== "invalid" && emailMasked;
    return (
      <div className="dark flex min-h-screen flex-col items-center justify-center bg-background px-4 text-foreground">
        <Card className="haruki-fade-in w-full max-w-md">
          <CardContent className="space-y-5 p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                 style={{ background: "rgba(220, 80, 60, 0.12)" }}>
              <AlertTriangle className="h-6 w-6" style={{ color: "rgb(220, 80, 60)" }} />
            </div>
            <div className="text-sm" data-testid="magic-verify-error">{msg}</div>

            {canResend && resendState !== "sent" && (
              <>
                <div className="rounded-md border border-border bg-card/50 p-3 text-xs text-muted-foreground" data-testid="magic-verify-email-masked">
                  We&apos;ll email a fresh link to <span className="font-semibold text-foreground">{emailMasked}</span>
                </div>
                <Button
                  onClick={handleResend}
                  disabled={resendState === "sending"}
                  size="lg" className="w-full font-semibold"
                  style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                  data-testid="magic-verify-resend-button"
                >
                  {resendState === "sending" ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <>Send me a fresh link</>
                  )}
                </Button>
                <div className="text-[11px] leading-relaxed text-muted-foreground">
                  <strong>Tip to avoid this next time:</strong> right-click the sign-in button in your email &rarr; <em>Copy link</em> &rarr; paste it into a <strong>private / incognito</strong> window.
                </div>
              </>
            )}

            {resendState === "sent" && (
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm" data-testid="magic-verify-resend-sent">
                <MailCheck className="mx-auto mb-2 h-5 w-5 text-emerald-500" />
                Fresh sign-in link sent to <strong>{emailMasked}</strong>. Check your inbox in the next minute — and this time, please open it in a private / incognito window.
              </div>
            )}

            {!canResend && (
              <Button
                onClick={() => window.location.replace("/signin")}
                size="lg" className="w-full font-semibold"
                style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                data-testid="magic-verify-error-back-button"
              >
                Return to sign-in
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // After click — spinner while POST runs
  if (verifying) {
    return (
      <div className="dark flex min-h-screen flex-col items-center justify-center bg-background text-foreground">
        <Loader2 className="mb-3 h-5 w-5 animate-spin text-muted-foreground" />
        <div className="text-sm text-muted-foreground">Signing you in…</div>
      </div>
    );
  }

  // Valid token — show the confirm card. This is the required user-gesture
  // step that keeps email scanners (which don't click buttons) from consuming
  // the token before the real user does.
  return (
    <div className="dark flex min-h-screen flex-col items-center justify-center bg-background px-4 text-foreground">
      <Card className="haruki-fade-in w-full max-w-md">
        <CardContent className="space-y-6 p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
               style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
            <ShieldCheck className="h-6 w-6" style={{ color: "hsl(var(--haruki-leaf))" }} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest"
                 style={{ color: "hsl(var(--haruki-leaf))" }}>
              Haruki Health
            </div>
            <h1 className="mt-3 font-heading text-2xl font-bold">
              Confirm your sign-in
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Click the button below to securely sign in to your Haruki Health account.
              This link is single-use and will be consumed when you continue.
            </p>
          </div>
          <Button
            data-testid="magic-verify-confirm-button"
            onClick={handleConfirm}
            size="lg"
            className="w-full text-base font-semibold shadow-lg"
            style={{ background: "hsl(var(--haruki-leaf))", color: "white",
                     boxShadow: "0 6px 24px -6px hsl(var(--haruki-leaf) / 0.5)" }}
          >
            Continue to Haruki <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="text-xs text-muted-foreground">
            Didn&apos;t request this? You can safely close this window.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
